// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import cloneDeep from "lodash.clonedeep";

import { TimePeriod } from "types";
import {
  ActionType,
  IAction,
  IActionCloudIoTrendData,
  IActionCloudTrendData,
  IActionCloudTrendDataRow,
  IActionCloudTrendInfo,
  IActionControllerTrendData,
  IActionControllerTrendInfo,
  IActionNewChargerStatusNotification,
  IActionNewChargerData,
  IActionTypeChargerStateChangeList,
} from "actions";
import { ILogDataIo, ILogDataTrend, ILogInfo } from "comtypes";
import { IIoTrend, INITIAL_STATE, IStateTrending, ITrendData } from "states/trendingstates";
import { isSet } from "utils";
import { IState } from "states/states";
import { dbAddr2ParaAddr, ICloudParameter, IInput, IOutput } from "parameter";
import { Aggregation } from "graphql/api";
import { IO_TREND_LIMIT } from "../dbtypes";

// uses webpack definitions
declare const IS_CLOUD: boolean;

function translateLogInfo(opcdata: ILogInfo[]) {
  // loop through the loginfo array
  opcdata.forEach((logInfo) => {
    // translate variable name
    Object.keys(logInfo.itemNames).forEach((curIndex) => {
      const idx = parseInt(curIndex);
      let name = logInfo.itemNames[idx];
      // trim the variable string
      name = name.replace("NVTrend_", "");
      name = name.replace("NV_", "");
      name = name.replace("Trend_", "");
      name = name.replace(/_Reg$/, "");
      // update name
      logInfo.itemNames[idx] = name;
    });
  });
  return opcdata;
}

function getTlIndex(
  mainSite: number | undefined,
  parameter: ICloudParameter,
  sourceSite: number,
  sourceTlId: number,
) {
  // if source is the main site, just take the tlId
  let tlIndex: number | undefined;
  // main site?
  if (sourceSite === mainSite) {
    parameter.tariffline.some((curItem, curIndex) => {
      if ((curItem.id ?? curIndex) === sourceTlId) {
        tlIndex = curIndex;
        return true;
      }
      return false;
    });
  } else {
    // check for matching virtual site entry
    parameter.virtualSite?.tariffline?.some((curAssign) => {
      if (curAssign.sourceSite === sourceSite && curAssign.sourceIndex === sourceTlId) {
        tlIndex = curAssign.destIndex!;
        return true;
      }
      return false;
    });
  }
  return tlIndex;
}

function addControllerTrendData(logData: ILogDataTrend[], trendData: ITrendData) {
  logData.forEach((logRow) => {
    const timestamp = Math.round(logRow.timestamp);
    // ensure existing array element
    if (!trendData.hasOwnProperty(timestamp)) {
      trendData[timestamp] = [];
    }
    // store data
    trendData[timestamp][logRow.tlId] = {
      regperiod: logRow.data.regperiod,
      costs: logRow.data.costs,
      tariff: logRow.data.tariff,
      // the transferred "energy" from the engine is no energy, but the period power!
      power: logRow.data.energy,
      energy: (logRow.data.energy * logRow.data.regperiod) / 3600,
      targetpower: logRow.data.targetEnergy,
    };
  });
}

// ioTrend source index: 0...input, 1...ouput
function addControllerIoData(
  logData: ILogDataIo[],
  ioTrend: IIoTrend,
  sourceIndex: number,
  parameter: IInput[] | IOutput[],
  isOutput: boolean,
) {
  logData.forEach((curRow) => {
    const inAddr = dbAddr2ParaAddr(curRow.data, isOutput);
    const index = parameter.findIndex(
      (curItem) =>
        curItem.bus === inAddr.bus &&
        curItem.module === inAddr.module &&
        curItem.channel === inAddr.channel,
    );
    if (isSet(index)) {
      const timestamp = Math.round(curRow.timestamp); // timestamps are floats in embyt devices
      // ensure timestamp entry
      if (!Array.isArray(ioTrend[timestamp])) {
        ioTrend[timestamp] = [];
      }
      // ensure input/output structure
      if (!Array.isArray(ioTrend[timestamp][sourceIndex])) {
        ioTrend[timestamp][sourceIndex] = [];
      }
      ioTrend[timestamp][sourceIndex][index] = curRow.data.value;
    }
  });
}

export default function trendingReducer(
  state: IStateTrending,
  action: IAction<any>,
  rootState: IState,
): IStateTrending {
  // type convert action string back into ActionType
  switch (action.type) {
    case ActionType.ACT_DISCONNECT_SITE:
      state = cloneDeep(INITIAL_STATE);
      return state;

    case ActionType.ACT_GET_TRENDINFO_SUCC:
      if (IS_CLOUD) {
        const payload = action.payload as IActionCloudTrendInfo;
        state.minTrendTimestamp = payload.minTrendTimestamp;
        state.maxTrendTimestamp = payload.maxTrendTimestamp;
        state.minIoTimestamp =
          isSet(payload.minInputTimestamp) && isSet(payload.minOutputTimestamp)
            ? Math.min(payload.minInputTimestamp, payload.minOutputTimestamp)
            : isSet(payload.minInputTimestamp)
            ? payload.minInputTimestamp
            : payload.minOutputTimestamp;
        state.maxIoTimestamp =
          isSet(payload.maxInputTimestamp) && isSet(payload.maxOutputTimestamp)
            ? Math.max(payload.maxInputTimestamp, payload.maxOutputTimestamp)
            : isSet(payload.maxInputTimestamp)
            ? payload.maxInputTimestamp
            : payload.maxOutputTimestamp;
      } else {
        const payload = action.payload as IActionControllerTrendInfo;
        state.totalCnt = payload.itemCnt;
        state.nextReadSeq = payload.itemCnt.map(() => payload.startDate);
      }
      return state;

    case ActionType.ACT_GET_CONTROLLERTREND_SUCC: {
      const payload = action.payload as IActionControllerTrendData;
      // index mapping: ["TariffLine", "Output", "Input"]
      if (payload.index === 0) {
        addControllerTrendData(payload.data as ILogDataTrend[], state.trendData);
      } else {
        console.error("unhandled trend index");
      }
      if (payload.data.length) {
        state.nextReadSeq![payload.index] = payload.data[payload.data.length - 1].timestamp * 1000; // s -> ms
      }
      return state;
    }

    case ActionType.ACT_GET_CONTROLLERIOTREND_SUCC: {
      const payload = action.payload as IActionControllerTrendData | undefined;
      // index mapping: ["TariffLine", "Output", "Input"]
      if (payload !== undefined && payload.index !== 0) {
        // ioTrend source index: 0...input, 1...ouput
        const sourceIndex = 2 - payload.index;
        const isOutput = sourceIndex === 1;
        const parameter = isOutput ? rootState.parameter!.output : rootState.parameter!.input;
        addControllerIoData(
          payload.data as ILogDataIo[],
          state.ioTrendFull,
          sourceIndex,
          parameter,
          isOutput,
        );
      } else {
        if (payload) {
          console.error("unhandled trend index");
        }
      }
      if (payload?.data.length) {
        state.nextReadSeq![payload.index] = payload.data[payload.data.length - 1].timestamp * 1000; // s -> ms
      }
      return state;
    }

    case ActionType.ACT_GET_CHARGERTREND_SUCC: {
      const payload = action.payload as IActionCloudTrendDataRow[];
      // clear potentially present charger data to keep memory footprint low
      state.chargerTrend = {};
      payload.forEach((curRow) => {
        if (isSet(curRow.chargerId)) {
          const connector = curRow.tlId;
          if (!isSet(connector)) {
            console.warn("cloud trend for foreign charger", curRow);
            return;
          }
          // ensure existing charger trend
          if (!state.chargerTrend![curRow.chargerId]) {
            state.chargerTrend![curRow.chargerId] = {};
          }
          // ensure existing timestmp
          if (!state.chargerTrend![curRow.chargerId][curRow.timestamp]) {
            state.chargerTrend![curRow.chargerId][curRow.timestamp] = [];
          }
          // store data
          state.chargerTrend![curRow.chargerId][curRow.timestamp][connector] = curRow.data;
        } else {
          console.warn("no charger id in cloud trend", curRow);
        }
      });
      return state;
    }

    case ActionType.ACT_GET_TREND_REQ:
      // before we load a new cloud trend, we delete all existing data to
      // not mix data with different aggregations
      if (IS_CLOUD) {
        state.trendData = {};
      }
      return state;

    case ActionType.ACT_GET_CLOUDTREND_SUCC: {
      const payload = action.payload as IActionCloudTrendData;
      payload.data.forEach((curRow) => {
        if (!isSet(curRow.chargerId)) {
          // for virtual sites, we may need to derive the destination tlId
          const tlIndex = getTlIndex(
            rootState.cached.selectedSite,
            rootState.parameter as ICloudParameter,
            curRow.siteId,
            curRow.tlId,
          );
          if (!isSet(tlIndex)) {
            console.warn("cloud trend for foreign tariff line", curRow);
            return;
          }
          // ensure existing timestmp
          if (!state.trendData[curRow.timestamp]) {
            state.trendData[curRow.timestamp] = [];
          }
          // store data
          state.trendData[curRow.timestamp][tlIndex] = curRow.data;
        } else {
          console.warn("charger id in cloud trend", curRow);
        }
      });
      return state;
    }

    case ActionType.ACT_APPEND_CLOUDTREND: {
      const payload = action.payload as IActionCloudTrendDataRow;
      // we only append cloud trends for un-aggregated views at end of scale
      if (
        rootState.gui.trendPeriod.timePeriod === TimePeriod.DAY &&
        rootState.gui.trendPeriod.startDate === undefined
      ) {
        const tlIndex = getTlIndex(
          rootState.cached.selectedSite,
          rootState.parameter as ICloudParameter,
          payload.siteId,
          payload.tlId,
        );
        // we might got a cloud trend of a non-subscribed tariff line
        if (isSet(tlIndex)) {
          // ensure existing timestmp
          if (!state.trendData[payload.timestamp]) {
            state.trendData[payload.timestamp] = [];
          }
          // store data
          state.trendData[payload.timestamp][tlIndex] = payload.data;
        }
      }
      return state;
    }

    case ActionType.ACT_GET_TREND_FAIL:
      // delete trend data to avoid showing old series
      state.trendData = {};
      return state;

    case ActionType.ACT_CHANGE_TREND_SUCC: {
      const payload = action.payload as IActionCloudTrendDataRow;
      // ensure existing timestmp
      if (!state.trendData[payload.timestamp]) {
        state.trendData[payload.timestamp] = [];
      }
      // store data
      state.trendData[payload.timestamp][payload.tlId] = payload.data;
      return state;
    }

    case ActionType.ACT_GET_CLOUDIOTREND_SUCC: {
      const payload = action.payload as IActionCloudIoTrendData;
      // before we store a new cloud trend, we delete all existing data to
      // not mix data with different aggregations
      state.ioTrendAggr = {};
      // we would not need to delete ioTrendFull as adding data there is always consistent/OK
      // but we can do it anyway to free up memory
      state.ioTrendFull = {};
      // clear TrendCount - otherwise we still see the month items in year-view
      state.ioTrendCount = {};
      // select trend storage to use
      const storage =
        payload.aggregation === Aggregation.NONE ? state.ioTrendFull : state.ioTrendAggr;
      const countStorage = state.ioTrendCount;

      payload.data.forEach((curRow) => {
        // ensure timestamp entry
        if (!Array.isArray(storage[curRow.timestamp])) {
          storage[curRow.timestamp] = [];
        }
        // ensure input/output structure
        const sourceIndex = curRow.source === "input" ? 0 : 1;
        if (!Array.isArray(storage[curRow.timestamp][sourceIndex])) {
          storage[curRow.timestamp][sourceIndex] = [];
        }
        storage[curRow.timestamp][sourceIndex][curRow.index] = curRow.value;

        if (!Array.isArray(countStorage[curRow.timestamp])) {
          countStorage[curRow.timestamp] = [];
        }
        if (payload.aggregation !== Aggregation.NONE && curRow.count != null) {
          if (!Array.isArray(countStorage[curRow.timestamp][sourceIndex])) {
            countStorage[curRow.timestamp][sourceIndex] = [];
          }
          countStorage[curRow.timestamp][sourceIndex][curRow.index] = curRow.count!;
        }
      });
      // set info if we are querying more data...
      state.queryMoreData = payload.queryMoreData;

      // check for limited response, in main io trend result first
      state.isIoTrendRestricted = payload.data.length >= IO_TREND_LIMIT;

      return state;
    }

    // if our main view shows aggregated data, we also get non-aggregated data for digital IOs
    case ActionType.ACT_GET_CLOUDIOTREND_FULL_SUCC: {
      const payload = action.payload as IActionCloudIoTrendData;
      payload.data.forEach((curRow) => {
        // ensure timestamp entry
        if (!Array.isArray(state.ioTrendFull[curRow.timestamp])) {
          state.ioTrendFull[curRow.timestamp] = [];
        }
        // ensure input/output structure
        const sourceIndex = curRow.source === "input" ? 0 : 1;
        if (!Array.isArray(state.ioTrendFull[curRow.timestamp][sourceIndex])) {
          state.ioTrendFull[curRow.timestamp][sourceIndex] = [];
        }
        state.ioTrendFull[curRow.timestamp][sourceIndex][curRow.index] = curRow.value;
      });
      // check for limited response, also in second full data, which is necessarily bigger
      state.isIoTrendRestricted = payload.data.length >= IO_TREND_LIMIT;
      state.queryMoreData = payload.queryMoreData;
      return state;
    }

    case ActionType.ACT_GET_IOTREND_FAIL:
      // delete trend data to avoid showing old series
      state.ioTrendFull = {};
      state.ioTrendAggr = {};
      state.ioTrendCount = {};
      return state;

    case ActionType.ACT_GET_CHARGING_STATES_SUCC: {
      const payload = action.payload as IActionTypeChargerStateChangeList;
      state.chargerStateData = payload;
      return state;
    }

    case ActionType.ACT_NEW_OCPP_DATA: {
      const payload = action.payload as IActionNewChargerData;
      const selectedChargingSession = rootState.ocpp.pendingChargingSession.find(
        (curItem) => curItem.id === rootState.router.transaction,
      );
      // check whether we are currently viewing this session
      if (
        isSet(selectedChargingSession) &&
        selectedChargingSession.fk_charger === payload.chargerId &&
        selectedChargingSession.connector === payload.connector
      ) {
        // ensure existing data structure
        if (!state.chargerTrend) {
          state.chargerTrend = {};
        }
        if (!state.chargerTrend[payload.chargerId]) {
          state.chargerTrend[payload.chargerId] = {};
        }
        // did we get new metering values?
        if (isSet(payload.power)) {
          // identify last data entry
          // do NOT use very latest but use the latest before the current entry
          const lastTimestamp = Object.keys(state.chargerTrend[payload.chargerId])
            .map((curItem) => parseInt(curItem))
            .filter(
              (curTimestamp) =>
                state.chargerTrend![payload.chargerId][curTimestamp]?.[payload.connector] &&
                curTimestamp < payload.timestamp,
            )
            .sort((a, b) => a - b)
            .pop();
          if (!lastTimestamp) {
            console.warn("first power timestamp?", payload, state.chargerTrend[payload.chargerId]);
          }
          // ensure existing timestmp
          if (!state.chargerTrend[payload.chargerId][payload.timestamp]) {
            state.chargerTrend[payload.chargerId][payload.timestamp] = [];
          }
          // take 30 s as default for very first entry, which should not happen anyway
          const regperiod = lastTimestamp ? payload.timestamp - lastTimestamp : 30;
          // store data
          state.chargerTrend[payload.chargerId][payload.timestamp][payload.connector] = {
            regperiod,
            energy: (payload.power * regperiod) / 3600,
            power: payload.power,
            soc: payload.soc,
          };
        }
      }
      return state;
    }

    case ActionType.ACT_NEW_CHARGER_STATUS: {
      const payload = action.payload as IActionNewChargerStatusNotification;
      const selectedChargingSession = rootState.ocpp.pendingChargingSession.find(
        (curItem) => curItem.id === rootState.router.transaction,
      );
      // check whether we are currently viewing this session
      if (
        isSet(selectedChargingSession) &&
        selectedChargingSession.fk_charger === payload.chargerId &&
        (selectedChargingSession.connector === payload.connector || payload.connector === 0)
      ) {
        // did we get a new connector state?
        // we just collect state changes from connector, but not from station (connector 0) here...
        if (isSet(payload.statusCode)) {
          if (state.chargerStateData === undefined) state.chargerStateData = [];
          state.chargerStateData.push({
            id: -1, // DB table id not provided and not needed
            fk_charger: payload.chargerId,
            connector: payload.connector,
            timestamp: payload.timestamp,
            source: payload.source,
            statusCode: payload.statusCode,
            errorCode: payload.errorCode,
            info: payload.info,
          });
        }
      }
      return state;
    }

    default:
      return state;
  }
}
